import React, { useMemo, useRef, useState } from 'react';
import { serialize } from 'cookie';
import { Alert, Box, FilterIcon } from 'glints-aries';
import { Button } from 'glints-aries/lib/General/Button/Button';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { GTMSetExperimentationInfo } from 'src/actions/googleTagManager';
import {
  CookieKeys,
  LocalStorageKeys,
  PAST_SEARCH_COOKIE_MAX_AGE,
} from 'src/common/constants';
import useConfig from 'src/common/hooks/useConfig';
import { useScrollAndOpenLoginSignUpPopup } from 'src/common/hooks/useScrollAndOpenLoginSignUpPopup';
import InfiniteScroll from 'src/components/InfiniteScroll';
import Spinner from 'src/components/LoadMore/Spinner';
import { MetadataRenderer } from 'src/components/MetaDescription/MetadataRenderer';
import { getSearchResultRobotsMeta } from 'src/components/MetaDescription/searchResultMetadata';
import { EBreakpoints, Media } from 'src/media';
import { getCountryWithSGFallback } from 'src/modules/ClientConfig/Selectors';
import { MainHeaderHeightContext } from 'src/modules/MainContainer/Context';
import { JobTabBarHeight } from 'src/modules/Opportunities/components/JobTabNavigation/constants';
import { LoginNudgeViewContainer } from 'src/modules/Opportunities/pages/Explore/ExploreTab.sc';
import { useGetSearchJobs } from 'src/modules/Opportunities/pages/Explore/hooks/useGetSearchJobs';
import { infiniteScrollMessage } from 'src/modules/Opportunities/pages/Explore/messages';
import { SlugTypes } from 'src/modules/Opportunities/utils/slugUrlParams';
import {
  FIRST_PAGE,
  PaginationRouterQueryKey,
} from 'src/modules/Profile/PsychFlatProfilePage/constants';
import { getIsAuthenticated } from 'src/modules/Session/Selectors';

import {
  JobSearchFiltersQueryData,
  SearchJobsQueryData,
} from '../../../graphqlUtils';
import { getQueryValue } from '../../../helpers';
import { useGetHierarchicalLocationsBySlug } from '../../../hooks/useGetHierarchicalLocationBySlug';
import { useGetMetaInfoForInternalSearchExplorePage } from '../../../hooks/useGetMetaInfoForInternalSearchExplorePage';
import {
  PopularSearch,
  SearchJobsResponse,
  SearchRouterQuery,
  SearchRouterQueryKey,
  SearchSuggestionsData,
} from '../../../types';
import { SEOSlugUrlParams } from '../../FilterSection/context';
import { FilterMobileModal } from '../../FilterSection/styles.sc';
import {
  JobFilterState,
  JobSearchFiltersResponse,
} from '../../FilterSection/types';
import { getSelectedOptionCountFromFilterState } from '../../FilterSection/utils/getSelectedOptionCountFromFilterState';
import { UpdatePastSearchesStateContext } from '../../SearchSuggestions/Context';
import { getSlugQueryForInternalSearchQueryPage } from './helpers';
import * as S from './InternalSearchExploreTab.sc';

export interface QueryExploreProps {
  jobSearchQuery: SearchJobsQueryData;
  jobSearchResponse?: SearchJobsResponse;
  filterQuery?: JobSearchFiltersQueryData;
  filterResponse?: JobSearchFiltersResponse;
  searchSuggestions?: SearchSuggestionsData;
}

const QueryExploreJobsList = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/QueryExploreJobsList'
  ).then((mod) => mod.QueryExploreJobsList)
);

const SearchFieldContainerMobile = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/SearchSection/SearchSectionMobile/MainSearchField'
  ).then((mod) => mod.MainSearchField)
);

const SearchFieldContainerDesktop = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/SearchSection/SearchSectionDesktop/SearchContainer'
  ).then((mod) => mod.SearchContainer)
);

const JobListEmptyView = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/JobListEmptyView'
  ).then((mod) => mod.JobListEmptyView)
);

const LoginNudgeView = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/LoginNudgeView'
  ).then((mod) => mod.LoginNudgeView)
);

const MobileAppDownloadQRBanner = dynamic(() =>
  import(
    'src/modules/CandidateMobileApp/Components/MobileAppDownloadBanner/MobileAppDownloadQRBanner'
  ).then((mod) => mod.MobileAppDownloadQRBanner)
);

const FilterSection = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/FilterSection'
  ).then((mod) => mod.FilterSection)
);

const SearchSuggestions = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/SearchSuggestions'
  ).then((mod) => mod.SearchSuggestions)
);
const Heading = dynamic(() =>
  import(
    'src/modules/Opportunities/pages/Explore/Components/Pages/InternalSearchExplorePage/SearchKeywordHeading'
  ).then((mod) => mod.SearchKeywordHeading)
);

export const InternalSearchExploreTab: NextPage<QueryExploreProps> = ({
  jobSearchQuery,
  jobSearchResponse,
  filterResponse,
  searchSuggestions,
}) => {
  const router = useRouter();
  const { query } = router;

  const {
    [SearchRouterQueryKey.Location]: locationFromFilter = undefined,
    [SearchRouterQueryKey.LocationId]: locationIdFromFilter = undefined,
    [PaginationRouterQueryKey.Page]: pageNumber = '1',
  } = router.query;

  const countryCode = useSelector(getCountryWithSGFallback);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const metadata = useGetMetaInfoForInternalSearchExplorePage();

  useScrollAndOpenLoginSignUpPopup(
    LocalStorageKeys.loginPopUpShownInExplorePage
  );

  const { hierarchicalLocation: locationResponse } =
    useGetHierarchicalLocationsBySlug(query);
  const locationId =
    (locationResponse && locationResponse?.id) ||
    getQueryValue(locationIdFromFilter);
  const locationName =
    (locationResponse && locationResponse?.formattedName) ||
    getQueryValue(locationFromFilter);

  const slugQuery = useMemo(() => {
    return getSlugQueryForInternalSearchQueryPage(
      jobSearchQuery,
      locationName,
      locationId
    );
  }, [jobSearchQuery, locationId, locationName]);

  const mainHeaderHeight = React.useContext(MainHeaderHeightContext);
  const topTabBarHeight = isAuthenticated
    ? mainHeaderHeight + JobTabBarHeight
    : mainHeaderHeight;

  const config = useConfig();
  const link = `${config.AIR_BRIDGE_LINK}/qrcode-explore`;

  const page = parseInt(pageNumber as string) || 1;

  const [showError, setShowError] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedOptionCount, setSelectedOptionCount] = useState(0);
  const [currentSearchSuggestions, setCurrentSearchSuggestions] =
    useState(searchSuggestions);
  const currentPage = useRef<number>(page);
  const [errorWhileFetchingMoreJobs, setErrorWhileFetchingMoreJobs] =
    useState(null);
  const [isLoadingMoreJobs, setIsLoadingMoreJobs] = useState(false);

  const handleJobFilterStateUpdate = React.useCallback(
    (jobFilterState: JobFilterState) => {
      currentPage.current = page ?? FIRST_PAGE;
      setSelectedOptionCount(
        getSelectedOptionCountFromFilterState(jobFilterState)
      );
    },
    [page]
  );

  const updatePastJobSearches = (pastSearches: SearchRouterQuery[]) => {
    const filteredPastSearches = pastSearches.filter((search) =>
      Boolean(search?.keyword?.trim())
    );

    document.cookie = serialize(
      CookieKeys.pastJobSearchConditions,
      JSON.stringify(filteredPastSearches),
      {
        path: '/',
        maxAge: PAST_SEARCH_COOKIE_MAX_AGE,
        sameSite: 'lax',
        secure: process.env.NODE_ENV === 'production',
      }
    );
    const defaultPopularSearch: PopularSearch[] = [];

    const defaultSearchSuggestionsData: SearchSuggestionsData = {
      pastSearches,
      popularSearches: defaultPopularSearch,
    };

    setCurrentSearchSuggestions(
      (prevSuggestions = defaultSearchSuggestionsData) => {
        return {
          ...prevSuggestions,
          pastSearches: filteredPastSearches,
        };
      }
    );
  };

  const onGetSearchJobsCompleted = (data: SearchJobsResponse) => {
    const expInfo = data.searchJobsV3.expInfo;
    if (expInfo) dispatch(GTMSetExperimentationInfo(expInfo));
  };

  const {
    data: data,
    error: searchJobsError,
    loading: searchJobsLoading,
    fetchMore: fetchMore,
  } = useGetSearchJobs({
    page,
    jobSearchQuery,
    onCompleted: onGetSearchJobsCompleted,
  });

  const jobs =
    data?.searchJobsV3.jobsInPage ??
    jobSearchResponse?.searchJobsV3.jobsInPage ??
    [];

  const hasMore: boolean =
    data?.searchJobsV3.hasMore ??
    jobSearchResponse?.searchJobsV3.hasMore ??
    false;

  const onRetryClicked = async () => {
    await fetchMoreJobs();
  };

  const onLoadMore = async () => {
    if (searchJobsLoading || isLoadingMoreJobs) return;
    if (!hasMore) return;
    await fetchMoreJobs();
  };

  const fetchMoreJobs = async () => {
    setErrorWhileFetchingMoreJobs(null);
    setIsLoadingMoreJobs(true);

    try {
      const nextPage = currentPage.current + 1;

      await fetchMore({
        variables: {
          data: {
            ...jobSearchQuery,
            page: nextPage,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const prevData = prev.searchJobsV3;
          const newData = fetchMoreResult.searchJobsV3;
          return {
            ...prev,
            searchJobsV3: {
              ...prevData,
              jobsInPage: [...prevData.jobsInPage, ...newData.jobsInPage],
              hasMore: newData.hasMore,
            },
          };
        },
      });

      currentPage.current = nextPage;
    } catch (err) {
      setErrorWhileFetchingMoreJobs(err);
    } finally {
      setIsLoadingMoreJobs(false);
    }
  };

  const hasResult = Boolean(jobs.length);
  const isLoading = !hasResult && searchJobsLoading;
  const searchResultRobotsMeta = getSearchResultRobotsMeta({
    hasError: Boolean(searchJobsError),
    hasResolved: Boolean(data?.searchJobsV3 ?? jobSearchResponse?.searchJobsV3),
    hasResults: hasResult,
    isLoading: searchJobsLoading,
  });

  const isNotLoggedInError =
    // @ts-ignore
    errorWhileFetchingMoreJobs?.networkError?.result?.errors?.[0].extensions
      ?.code === 'NO_PERMISSION';

  const shouldRenderLoginNudgeBanner = !isAuthenticated && isNotLoggedInError;
  const shouldRenderExploreJobsList = hasResult;

  function JobListComponent() {
    if (isLoading) {
      return <Spinner />;
    }

    const LoginView = () => (
      <LoginNudgeViewContainer>
        <LoginNudgeView opportunities={jobs.slice(0, 4) ?? []} />
      </LoginNudgeViewContainer>
    );

    if (shouldRenderExploreJobsList) {
      const InfiniteScrollComponent = () => (
        <InfiniteScroll
          onLoadMore={onLoadMore}
          hasMore={hasMore && !errorWhileFetchingMoreJobs}
          hasLoadedAll={!hasMore && !errorWhileFetchingMoreJobs}
          loadingFormattedMessage={infiniteScrollMessage.loading}
          loadedAllFormattedMessage={infiniteScrollMessage.loaded}
          total={jobs.length ?? 0}
        />
      );

      const RetryButtonComponent = () => (
        <S.RetryButtonContainer>
          <Button variant="yellow" onClick={onRetryClicked}>
            <FormattedMessage
              id="app.modules.Opportunities.pages.Explore.ExploreTab.retry"
              defaultMessage="Retry"
            />
          </Button>
        </S.RetryButtonContainer>
      );

      return (
        <>
          <QueryExploreJobsList opportunities={jobs} />
          {shouldRenderLoginNudgeBanner ? (
            <LoginView />
          ) : errorWhileFetchingMoreJobs ? (
            <RetryButtonComponent />
          ) : (
            !searchJobsLoading && <InfiniteScrollComponent />
          )}
        </>
      );
    }

    if (shouldRenderLoginNudgeBanner) {
      return <LoginView />;
    }

    return (
      <JobListEmptyView
        slug={null}
        slugType={null}
        city={null}
        localizedCityName={null}
        localizedCategoryName={null}
      />
    );
  }

  return (
    <>
      {showError && (
        <Alert
          onClose={() => setShowError(false)}
          autoClose={3000}
          message={formatMessage(
            defineMessage({
              id: 'app.modules.Opportunities.pages.Explore.ExploreTab.searchJobsError',
              defaultMessage: 'Error while loading jobs. Please retry.',
            })
          )}
        />
      )}

      <MetadataRenderer
        {...metadata}
        name={{
          ...metadata.name,
          ...searchResultRobotsMeta,
        }}
        glintsTrackingName="explore"
      />

      <S.QueryExploreTabBody isJobTabShown={isAuthenticated}>
        <SEOSlugUrlParams.Provider
          value={{
            type: SlugTypes.INTERNAL_SEARCH,
            countryCode,
            isSlugUrl: true,
            slugQuery: slugQuery,
          }}
        >
          <Media lessThan={EBreakpoints.desktopS}>
            <S.MobileStickySearchAndFilterContainer
              topTabBarHeight={topTabBarHeight}
              isSearchModalOpen={isSearchModalOpen}
            >
              <Box py={16}>
                <UpdatePastSearchesStateContext.Provider
                  value={{ updatePastJobSearches }}
                >
                  <SearchFieldContainerMobile
                    onOpenModal={() => setIsSearchModalOpen(true)}
                    onCloseModal={() => setIsSearchModalOpen(false)}
                  />
                </UpdatePastSearchesStateContext.Provider>

                <Box mt={8}>
                  <S.MobileFilterButton
                    onClick={() => setIsFilterModalOpen(true)}
                    aria-label="Filter button"
                  >
                    {selectedOptionCount > 0 ? (
                      <S.MobileFilterButtonBadge
                        label={selectedOptionCount}
                        aria-label="Number of selected filter"
                      />
                    ) : (
                      <FilterIcon />
                    )}

                    <Box ml={8}>
                      <FormattedMessage
                        id="job-filter.filter-modal.open-button"
                        defaultMessage="Filter"
                      />
                    </Box>
                  </S.MobileFilterButton>
                </Box>
              </Box>
            </S.MobileStickySearchAndFilterContainer>
          </Media>
          <Media greaterThanOrEqual={EBreakpoints.desktopS}>
            <S.DesktopSearchBoxWrapper>
              <Box pb={16}>
                <UpdatePastSearchesStateContext.Provider
                  value={{ updatePastJobSearches }}
                >
                  <SearchFieldContainerDesktop />
                </UpdatePastSearchesStateContext.Provider>
              </Box>
            </S.DesktopSearchBoxWrapper>
          </Media>
          <SearchSuggestions
            pastSearches={currentSearchSuggestions?.pastSearches ?? []}
            popularSearches={currentSearchSuggestions?.popularSearches ?? []}
          />
          <Heading />
          <S.Body>
            <Media greaterThanOrEqual={EBreakpoints.desktopS}>
              <S.DesktopStickyFilterContainer topTabBarHeight={topTabBarHeight}>
                <MobileAppDownloadQRBanner
                  id="explore-tab-qr-banner"
                  link={link}
                />

                <S.FilterContainerBorder>
                  <FilterSection
                    isFilterModalOpen={isFilterModalOpen}
                    selectedOptionCount={selectedOptionCount}
                    onUpdate={handleJobFilterStateUpdate}
                    isDesktop={true}
                    initialFilters={filterResponse}
                  />
                </S.FilterContainerBorder>
              </S.DesktopStickyFilterContainer>
            </Media>
            <Media lessThan={EBreakpoints.desktopS}>
              <FilterMobileModal
                isVisible={isFilterModalOpen}
                hideHeader={true}
                keepChildrenMountedOnClose={true}
              >
                <FilterSection
                  isFilterModalOpen={isFilterModalOpen}
                  onCloseModal={() => setIsFilterModalOpen(false)}
                  selectedOptionCount={selectedOptionCount}
                  onUpdate={handleJobFilterStateUpdate}
                  isDesktop={false}
                  initialFilters={filterResponse}
                />
              </FilterMobileModal>
            </Media>

            <S.Flex>
              <JobListComponent />
            </S.Flex>
          </S.Body>
        </SEOSlugUrlParams.Provider>
      </S.QueryExploreTabBody>
    </>
  );
};
